import React, { useState, useEffect } from 'react';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from './ThemeProvider';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /** Order matches main scroll order (after #hero) */
  const navLinks = [
    { label: 'About me', href: '#about' },
    { label: 'Skills', href: '#skills' },
    { label: 'Experience', href: '#experience' },
    { label: 'Projects', href: '#projects' },
    { label: 'Live signal', href: '#live-signal' },
    { label: 'Demo', href: '#demo' },
    { label: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
        isScrolled ? 'top-4' : ''
      }`}
    >
      <div className={`px-6 py-4 rounded-full backdrop-blur-md transition-all ${
        isScrolled 
          ? 'bg-white/80 dark:bg-stone-900/80 shadow-lg border border-stone-200/50 dark:border-stone-700/50' 
          : 'bg-white/60 dark:bg-stone-900/60 border border-stone-200/30 dark:border-stone-700/30'
      }`}>
        <div className="flex items-center gap-4 lg:gap-6">
          <a
            href="#hero"
            onClick={(e) => scrollToSection(e, '#hero')}
            className="text-xl font-bold text-stone-900 dark:text-stone-100 hover:text-amber-800 dark:hover:text-amber-200 transition-colors"
          >
            Ankit Attri
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center flex-wrap justify-end gap-x-2 xl:gap-x-3 gap-y-1 max-w-[min(100%,42rem)] xl:max-w-none">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="text-[11px] xl:text-sm text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 transition-colors font-medium whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-stone-200/50 dark:hover:bg-stone-800/50 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5 text-stone-600 dark:text-stone-300" />
              ) : (
                <Sun className="w-5 h-5 text-stone-600 dark:text-stone-300" />
              )}
            </button>
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center space-x-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-stone-200/50 dark:hover:bg-stone-800/50"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? (
                <Moon className="w-5 h-5 text-stone-600" />
              ) : (
                <Sun className="w-5 h-5 text-stone-300" />
              )}
            </button>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-stone-200/50 dark:hover:bg-stone-800/50"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-stone-900 dark:text-stone-100" />
              ) : (
                <Menu className="w-6 h-6 text-stone-900 dark:text-stone-100" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <nav className="md:hidden mt-4 pt-4 border-t border-stone-200 dark:border-stone-700 space-y-3">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => scrollToSection(e, link.href)}
                className="block text-sm text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 transition-colors font-medium"
              >
                {link.label}
              </a>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
